// @flow
import React from "react";
import { type SearchFieldDefinitionDTO, FormLookupType } from "data/types";
// import CheckboxEditorControl from "components/ExtraAttributes/EditorControl/CheckboxEditorControl";
import TextEditorControl from "components/ExtraAttributes/EditorControl/TextEditorControl";
// import MemoEditorControl from "components/ExtraAttributes/EditorControl/MemoEditorControl";
import NumberEditorControl from "components/ExtraAttributes/EditorControl/NumberEditorControl";
import DateEditorControl from "components/ExtraAttributes/EditorControl/DateEditorControl";
// import RadioEditorControl from "components/ExtraAttributes/EditorControl/RadioEditorControl";
import LookupEditorControl from "components/ExtraAttributes/EditorControl/LookupEditorControl";

import GeoAreaEditorControl from "components/ExtraAttributes/EditorControl/GeoAreaEditorControl";
import GeoRadiusEditorControl from "./GeoRadiusEditorControl";
import RelativeDateEditorControl from "./RelativeDateEditorControl";
import { SearchValueType, SearchOperator, DateTimeFormat } from "data/types";
import styles from "./AttributeSearch.module.css";
// import * as s from "data/reducers/selectors";
import loc from "i18next";

import toBetweenEditor from "./toBetweenEditor";
import toSearchEditor from "./toSearchEditor";

type Props = {
    /** current itemUri */
    itemUri: string,
    /** current attribute definition */
    attribute?: SearchFieldDefinitionDTO,
    /** list of all attributes */
    attributes: Array<SearchFieldDefinitionDTO>,
    /** custom width */
    width?: number,
    /** callback when an attribute is edited
     * @param {string} name unique name of attribute being changed
     * @param {any} value new value
     */
    onChange: (name: string, value: any) => void,
    /** whether to show field's label */
    showLabel: boolean,
};

const formPrefix = "sf.";
const SearchLookupEditorControl = toSearchEditor(LookupEditorControl);
const SearchTextEditorControl = toSearchEditor(TextEditorControl);
const SearchNumberEditorControl = toSearchEditor(NumberEditorControl);
const SearchDateEditorControl = toSearchEditor(DateEditorControl);
const RelativeDateSearchControl = toSearchEditor(RelativeDateEditorControl);
const GeoSearchRadiusEditorControl = toSearchEditor(GeoRadiusEditorControl);
const GeoSearchAreaEditorControl = toSearchEditor(GeoAreaEditorControl);
const SearchBetweenNumberEditorControl = toSearchEditor(
    toBetweenEditor(NumberEditorControl, NumberEditorControl)
);
const SearchBetweenDateEditorControl = toSearchEditor(
    toBetweenEditor(DateEditorControl, DateEditorControl)
);

class AttributeSearch extends React.Component<Props> {
    /** re-render only when value or searchOperator changes! */
    /* istanbul ignore next*/ shouldComponentUpdate = (nextProps: Props) =>
        nextProps.attribute == null ||
        this.props.attribute == null ||
        nextProps.attribute.id !== this.props.attribute.id ||
        nextProps.attribute.value !== this.props.attribute.value ||
        nextProps.itemUri !== this.props.itemUri ||
        nextProps.attribute.op !== this.props.attribute.op;

    render() {
        const { itemUri, attribute, attributes, onChange, showLabel } =
            this.props;
        // if no attributeDefinition found then render nothing
        if (attribute == null) return null;

        //console.log('Attribute', attribute);
        // TODO header converter from name like CurionLib does
        const header = attribute.header || attribute.name;
        const value = attribute.value;

        switch (attribute.op) {
            case SearchOperator.Contextor:
            case SearchOperator.ContextorContains:
                return (
                    <SearchLookupEditorControl
                        key={`sa-${attribute.id}`}
                        searchOperator={attribute.op}
                        itemUri={itemUri}
                        name={formPrefix + attribute.name}
                        attributes={attributes}
                        lookupType={FormLookupType.sf_ftlookup}
                        valueType={attribute.valueType}
                        isMultiple={attribute.multiple}
                        useContains={true}
                        useKeys={false}
                        header={header}
                        value={value}
                        width={0}
                        isReadonly={false}
                        isRequired={attribute.required}
                        // validationErrors={getValidationErrors(attribute)}
                        onChange={onChange}
                        showLabel={showLabel}
                        values={attribute.extra && attribute.extra.values}
                        parentName={
                            attribute.extra &&
                            attribute.extra.parentLookupCommonName
                        }
                    />
                );

            case SearchOperator.FromTo:
            case SearchOperator.FromToWithTime:
                switch (attribute.valueType) {
                    case SearchValueType.Decimal:
                    case SearchValueType.DecimalFieldOnly:
                        return (
                            <SearchBetweenNumberEditorControl
                                key={`sa-${attribute.id}`}
                                searchOperator={attribute.op}
                                valueType={attribute.valueType}
                                className={styles.searchTextEditor}
                                name={formPrefix + attribute.name}
                                header={header}
                                value={value} //array [val1,val2]
                                width={0}
                                decimalScale={3}
                                isReadonly={false}
                                isRequired={attribute.required}
                                style={{ borderRadius: 0 }}
                                validationErrors={{
                                    isDefaultRequiredValue: loc.t(
                                        "common:validation.required"
                                    ),
                                }}
                                onChange={onChange}
                                showLabel={showLabel}
                                truncateZeros={true}
                            />
                        );

                    case SearchValueType.DateTime:
                        return (
                            <SearchBetweenDateEditorControl
                                key={`sa-${attribute.id}-${attribute.op}`}
                                searchOperator={attribute.op}
                                valueType={attribute.valueType}
                                name={formPrefix + attribute.name}
                                header={header}
                                value={value} //array [val1,val2]
                                type={
                                    attribute.op ===
                                    SearchOperator.FromToWithTime
                                        ? DateTimeFormat.DateTime
                                        : DateTimeFormat.Date
                                }
                                isReadonly={false}
                                isRequired={attribute.required}
                                // validationErrors={getValidationErrors(attribute)}
                                validations="isNotInvalid"
                                validationErrors={{
                                    isNotInvalid: loc.t("common:validate.date"),
                                }}
                                onChange={onChange}
                            />
                        );
                    default:
                        return null;
                }

            case SearchOperator.RelativeDate:
            case SearchOperator.FromRelative:
            case SearchOperator.ToRelative:
                return (
                    <RelativeDateSearchControl
                        key={`sa-${attribute.id}`}
                        index={attribute.id}
                        searchOperator={attribute.op}
                        className={styles.searchRelativeDataEditor}
                        valueType={attribute.valueType}
                        name={formPrefix + attribute.name}
                        header={header}
                        value={value}
                        width={0}
                        isRequired={attribute.required}
                        onChange={onChange}
                    />
                );

            case SearchOperator.Equals:
            case SearchOperator.Contains:
            case SearchOperator.StartsWith:
            case SearchOperator.EndsWith:
            case SearchOperator.From:
            case SearchOperator.FromWithTime:
            case SearchOperator.To:
            case SearchOperator.ToWithTime:
            case SearchOperator.EqualsWithTime:
                switch (attribute.valueType) {
                    case SearchValueType.Decimal:
                    case SearchValueType.DecimalFieldOnly:
                        return (
                            <SearchNumberEditorControl
                                key={`sa-${attribute.id}`}
                                searchOperator={attribute.op}
                                valueType={attribute.valueType}
                                placeholder={loc.t(
                                    "search:form.input.placeholder"
                                )}
                                className={styles.searchTextEditor}
                                name={formPrefix + attribute.name}
                                header={header}
                                value={value}
                                width={0}
                                decimalScale={3}
                                isReadonly={false}
                                isRequired={attribute.required}
                                // validationErrors={getValidationErrors(attribute)}
                                onChange={onChange}
                                showLabel={showLabel}
                                truncateZeros={true}
                            />
                        );
                    case SearchValueType.DateTime:
                        return (
                            <SearchDateEditorControl
                                key={`sa-${attribute.id}-${attribute.op}`}
                                searchOperator={attribute.op}
                                valueType={attribute.valueType}
                                name={formPrefix + attribute.name}
                                header={header}
                                value={value || ""}
                                type={
                                    [
                                        SearchOperator.FromWithTime,
                                        SearchOperator.ToWithTime,
                                        SearchOperator.EqualsWithTime,
                                    ].includes(attribute.op)
                                        ? DateTimeFormat.DateTime
                                        : DateTimeFormat.Date
                                }
                                isReadonly={false}
                                isRequired={attribute.required}
                                // validationErrors={getValidationErrors(attribute)}
                                validations="isNotInvalid"
                                validationErrors={{
                                    isNotInvalid: loc.t("common:validate.date"),
                                }}
                                onChange={onChange}
                                showLabel={showLabel}
                            />
                        );
                    case SearchValueType.NotSet:
                    case SearchValueType.String:
                    case SearchValueType.StringFieldOnly:
                        return (
                            <SearchTextEditorControl
                                key={`sa-${attribute.id}`}
                                searchOperator={attribute.op}
                                valueType={attribute.valueType}
                                placeholder={loc.t(
                                    "search:form.input.placeholder"
                                )}
                                name={formPrefix + attribute.name}
                                header={header}
                                value={value}
                                width={0}
                                isReadonly={false}
                                isRequired={attribute.required}
                                onChange={onChange}
                                showLabel={showLabel}
                                className={styles.searchTextEditor}
                            />
                        );
                    default:
                        return null;
                }

            case SearchOperator.GeoRadius:
            case SearchOperator.GeoRadiusIntersect:
            case SearchOperator.GeoRadiusWithin:
                return (
                    <GeoSearchRadiusEditorControl
                        key={`sa-${attribute.id}`}
                        index={attribute.id}
                        searchOperator={attribute.op}
                        valueType={attribute.valueType}
                        name={formPrefix + attribute.name}
                        header={header}
                        value={value}
                        width={0}
                        onChange={onChange}
                        className="geosearch-radius"
                        isRequired={attribute.required}
                        placeholder={loc.t(
                            "search:form.input.placeholderGeoRadius"
                        )}
                    />
                );
            case SearchOperator.GeoArea:
            case SearchOperator.GeoAreaIntersect:
            case SearchOperator.GeoAreaWithin:
            case SearchOperator.GeoAreaPoint:
                return (
                    <GeoSearchAreaEditorControl
                        key={`sa-${attribute.id}`}
                        searchOperator={attribute.op}
                        valueType={attribute.valueType}
                        placeholder={loc.t(
                            "search:form.input.placeholderGeoArea"
                        )}
                        name={formPrefix + attribute.name}
                        header={header}
                        value={value}
                        width={0}
                        isReadonly={false}
                        isRequired={attribute.required}
                        onChange={onChange}
                        showLabel={showLabel}
                        className={styles.searchTextEditor}
                    />
                );

            case SearchOperator.NotSet:
                // special Freetext attribute?
                if (
                    attribute.valueType === SearchValueType.NotSet &&
                    attribute.name === "$SEARCHTEXT$"
                ) {
                    return (
                        <SearchTextEditorControl
                            key={`sa-${attribute.id}`}
                            searchOperator={attribute.op}
                            valueType={attribute.valueType}
                            placeholder={loc.t(
                                "search:form.input.placeholderSearchText"
                            )}
                            name={formPrefix + attribute.name}
                            header={header}
                            value={value}
                            width={0}
                            isReadonly={false}
                            isRequired={attribute.required}
                            onChange={onChange}
                            showLabel={showLabel}
                            className={styles.searchTextEditor}
                        />
                    );
                }
            //lint -fallthrough
            default:
                console.warn(
                    `TODO for SearchValueType: ${attribute.valueType}, SearchOperator: ${attribute.op}`
                );
                return null;
        }
    }
}
export default AttributeSearch;
